import { SELF_ALLOCATION_RETIRED } from "@/features/finances/selfAllocationFeature";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useSelfAllocations,
  useApproveSelfAllocation,
  useRejectSelfAllocation,
  useSelfAllocationMissing,
} from "@/features/finances/hooks/useSelfAllocation";
import type {
  SelfAllocationMissingRow,
  SelfAllocationRequest,
  SelfAllocationStatus,
} from "@/features/finances/types/selfAllocation";
import { fractionsAreGridFriendly } from "@/features/finances/components/SelfAllocationGridEditor";
import { naturalSortKey } from "@/utils/naturalSort";
import { BackToFridayLink } from "@/features/tasks/components/BackToFridayLink";

type AdminTabKey = SelfAllocationStatus | "missing";

const MONTHS_PL = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];

const ACTIVE_TABS: { key: AdminTabKey; label: string; shortLabel: string }[] = [
  { key: "submitted", label: "Do zatwierdzenia", shortLabel: "Nowe" },
  { key: "approved", label: "Zatwierdzone", shortLabel: "OK" },
  { key: "rejected", label: "Odrzucone", shortLabel: "Odrz." },
  { key: "draft", label: "Szkice (pracowników)", shortLabel: "Szkice" },
  { key: "missing", label: "Brakujące", shortLabel: "Brak" },
];

/* Archiwum po wycofaniu funkcji: bez „Brakujących" (nikt już niczego nie
   składa) i bez decyzji — złożone, których nikt nie zdążył rozpatrzyć, zostają
   jako „Nierozpatrzone". */
const ARCHIVE_TABS: { key: AdminTabKey; label: string; shortLabel: string }[] = [
  { key: "approved", label: "Zatwierdzone", shortLabel: "OK" },
  { key: "rejected", label: "Odrzucone", shortLabel: "Odrz." },
  { key: "submitted", label: "Nierozpatrzone", shortLabel: "Nieroz." },
  { key: "draft", label: "Szkice", shortLabel: "Szkice" },
];

const TABS = SELF_ALLOCATION_RETIRED ? ARCHIVE_TABS : ACTIVE_TABS;

function previousMonth(today = new Date()): { year: number; month: number } {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  if (m === 1) return { year: y - 1, month: 12 };
  return { year: y, month: m - 1 };
}

function formatPeriod(year: number, month: number) {
  return `${MONTHS_PL[month - 1]} ${year}`;
}

function fmtDecimal(s: string) {
  return parseFloat(s || "0").toFixed(3);
}

export default function SelfAllocationAdminPage() {
  const [tab, setTab] = useState<AdminTabKey>(TABS[0].key);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const initialPeriod = useMemo(() => previousMonth(), []);
  const [missingYear, setMissingYear] = useState<number>(initialPeriod.year);
  const [missingMonth, setMissingMonth] = useState<number>(initialPeriod.month);

  const isMissingTab = tab === "missing";
  const { data, isLoading } = useSelfAllocations(
    isMissingTab ? undefined : { status: tab as SelfAllocationStatus },
    !isMissingTab,
  );
  const { data: missingData, isLoading: missingLoading } =
    useSelfAllocationMissing(missingYear, missingMonth, isMissingTab);
  const approveMut = useApproveSelfAllocation();
  const rejectMut = useRejectSelfAllocation();

  const items: SelfAllocationRequest[] = data?.data ?? [];

  if (!isMissingTab && isLoading) return <PageLoader />;

  const handleApprove = async (id: string) => {
    if (!confirm("Zatwierdzić wniosek? Istniejące alokacje za ten miesiąc zostaną nadpisane.")) return;
    await approveMut.mutateAsync(id);
  };

  const handleReject = async (id: string) => {
    const reason = rejectReason[id]?.trim();
    if (!reason || reason.length < 3) {
      alert("Podaj powód odrzucenia (minimum 3 znaki).");
      return;
    }
    await rejectMut.mutateAsync({ id, rejection_reason: reason });
    setRejectReason((prev) => ({ ...prev, [id]: "" }));
    setExpanded(null);
  };

  return (
    <div className="max-w-5xl mx-auto p-3 sm:p-6">
      {/* Widok wywołany z sekcji „Postęp prac" w Friday — droga powrotna.
          W archiwum (wejście z sidebara) nie ma dokąd wracać. */}
      {!SELF_ALLOCATION_RETIRED && <BackToFridayLink className="mb-2" />}
      {SELF_ALLOCATION_RETIRED ? (
        <>
          <h1 className="text-base sm:text-2xl font-semibold mb-2 sm:mb-1 leading-tight">
            <span className="sm:hidden">Rozkład pracy — archiwum</span>
            <span className="hidden sm:inline">Rozkład pracy — archiwum wniosków</span>
          </h1>
          <div
            className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="self-allocation-retired-banner"
          >
            Funkcja rozkładu pracy została wycofana. Poniżej wnioski pracowników z
            okresu jej działania — tylko do odczytu. Zatwierdzone przydziały są już
            zapisane jako rozkład pracy w finansach przedsięwzięć.
          </div>
        </>
      ) : (
        <>
          <h1 className="text-base sm:text-2xl font-semibold mb-2 sm:mb-1 leading-tight">
            <span className="sm:hidden">Wnioski o rozkład pracy</span>
            <span className="hidden sm:inline">Rozkład pracy — wnioski pracowników</span>
          </h1>
          <p className="text-sm text-gray-500 mb-4 hidden sm:block">
            Panel CEO — zatwierdzanie i odrzucanie wniosków pracowników o samodzielne
            wskazanie rozkładu czasu pracy na poszczególne przedsięwzięcia.
          </p>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-0 sm:gap-1 border-b border-gray-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex-1 sm:flex-none px-2 sm:px-3 py-2 text-xs sm:text-sm border-b-2 whitespace-nowrap ${
              tab === t.key
                ? "border-blue-600 text-blue-700 font-medium"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <span className="sm:hidden">{t.shortLabel}</span>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {isMissingTab ? (
        <MissingPanel
          year={missingYear}
          month={missingMonth}
          rows={missingData?.data ?? []}
          isLoading={missingLoading}
          onChangePeriod={(y, m) => {
            setMissingYear(y);
            setMissingMonth(m);
          }}
        />
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-12 text-center">Brak wniosków w tej zakładce.</p>
      ) : (
        <div className="space-y-3">
          {items.map((req) => {
            const isOpen = expanded === req.id;
            const total = parseFloat(req.lines_total_fraction || "0");
            return (
              <div
                key={req.id}
                className="border border-gray-200 rounded bg-white overflow-hidden"
                data-testid={`req-row-${req.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : req.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{req.employee_full_name}</div>
                    <div className="text-xs text-gray-500">
                      {formatPeriod(req.year, req.month)} · suma {Math.round(total * 100)}%
                      {req.submitted_at && (
                        <> · zgłoszono {new Date(req.submitted_at).toLocaleString("pl-PL")}</>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 shrink-0">{isOpen ? "▾" : "▸"}</span>
                </button>

                {isOpen && (
                  <div className="px-3 sm:px-4 py-3 border-t border-gray-100 bg-gray-50">
                    {req.notes && (
                      <div className="mb-3 text-sm">
                        <div className="text-xs text-gray-500 mb-0.5">Uwagi pracownika</div>
                        <div>{req.notes}</div>
                      </div>
                    )}
                    {fractionsAreGridFriendly(req.lines) && (
                      <GridPreview req={req} />
                    )}
                    {/* Mobile: card list (long project names need full width) */}
                    <ul className="sm:hidden bg-white border border-gray-200 rounded divide-y divide-gray-100">
                      {req.lines.map((line) => (
                        <li key={line.id} className="px-3 py-2">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-mono text-xs text-gray-500">
                              {line.project_short_name}
                            </span>
                            <span className="text-sm font-medium">
                              {Math.round(parseFloat(line.fraction) * 100)}%
                              <span className="ml-2 font-mono text-xs text-gray-400">
                                {fmtDecimal(line.fraction)}
                              </span>
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 break-words">
                            {line.project_name}
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* Desktop/tablet: table */}
                    <table className="hidden sm:table w-full text-sm bg-white border border-gray-200 rounded">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500">
                            Projekt
                          </th>
                          <th className="text-right px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 w-32">
                            Ułamek
                          </th>
                          <th className="text-right px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 w-20">
                            %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {req.lines.map((line) => (
                          <tr key={line.id}>
                            <td className="px-3 py-1.5">
                              <span className="font-mono text-xs text-gray-500">
                                {line.project_short_name}
                              </span>{" "}
                              {line.project_name}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono">
                              {fmtDecimal(line.fraction)}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              {Math.round(parseFloat(line.fraction) * 100)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {SELF_ALLOCATION_RETIRED && req.status === "submitted" && (
                      <p className="mt-3 text-sm text-gray-500">
                        Wniosek nie został rozpatrzony przed wycofaniem funkcji —
                        pozostaje w archiwum jako złożony.
                      </p>
                    )}

                    {!SELF_ALLOCATION_RETIRED && req.status === "submitted" && (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                        <Button
                          size="sm"
                          loading={approveMut.isPending}
                          onClick={() => handleApprove(req.id)}
                          data-testid={`btn-approve-${req.id}`}
                        >
                          Zatwierdź
                        </Button>
                        <input
                          type="text"
                          placeholder="Powód odrzucenia"
                          value={rejectReason[req.id] ?? ""}
                          onChange={(e) =>
                            setRejectReason((p) => ({ ...p, [req.id]: e.target.value }))
                          }
                          data-testid={`reject-reason-${req.id}`}
                          className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={rejectMut.isPending}
                          onClick={() => handleReject(req.id)}
                          data-testid={`btn-reject-${req.id}`}
                        >
                          Odrzuć
                        </Button>
                      </div>
                    )}

                    {req.status === "rejected" && req.rejection_reason && (
                      <div className="mt-3 text-sm">
                        <span className="text-xs text-gray-500">Powód odrzucenia:</span>{" "}
                        {req.rejection_reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PREVIEW_COLORS = [
  "bg-amber-100 border-amber-400 text-amber-900",
  "bg-sky-100 border-sky-400 text-sky-900",
  "bg-emerald-100 border-emerald-400 text-emerald-900",
  "bg-rose-100 border-rose-400 text-rose-900",
  "bg-violet-100 border-violet-400 text-violet-900",
  "bg-orange-100 border-orange-400 text-orange-900",
  "bg-teal-100 border-teal-400 text-teal-900",
  "bg-fuchsia-100 border-fuchsia-400 text-fuchsia-900",
];

function MissingPanel({
  year,
  month,
  rows,
  isLoading,
  onChangePeriod,
}: {
  year: number;
  month: number;
  rows: SelfAllocationMissingRow[];
  isLoading: boolean;
  onChangePeriod: (year: number, month: number) => void;
}) {
  // Allow auditing any month back to the configured minimum (2026-01) up to
  // the current calendar month.
  const yearOptions = useMemo(() => {
    const now = new Date();
    const years: number[] = [];
    for (let y = 2026; y <= now.getFullYear(); y++) years.push(y);
    return years;
  }, []);

  return (
    <div data-testid="missing-panel">
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-gray-50 border border-gray-200 rounded p-3">
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-0.5">Rok</div>
          <select
            value={year}
            onChange={(e) => onChangePeriod(parseInt(e.target.value, 10), month)}
            data-testid="missing-year"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-0.5">Miesiąc</div>
          <select
            value={month}
            onChange={(e) => onChangePeriod(year, parseInt(e.target.value, 10))}
            data-testid="missing-month"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {MONTHS_PL.map((name, idx) => (
              <option key={idx} value={idx + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs text-gray-500 ml-auto">
          Pracownicy z uprawnieniem samo-alokacji, którzy nie złożyli wniosku za
          wskazany miesiąc (lub mają go w szkicu / odrzucony).
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-12 text-center">Ładowanie…</p>
      ) : rows.length === 0 ? (
        <p
          className="text-sm text-gray-400 py-12 text-center"
          data-testid="missing-empty"
        >
          Wszyscy uprawnieni pracownicy złożyli wniosek za {MONTHS_PL[month - 1]} {year}.
        </p>
      ) : (
        <>
          <div className="text-xs text-gray-500 mb-2">
            {rows.length} {rows.length === 1 ? "osoba" : "osób"} — {MONTHS_PL[month - 1]} {year}
          </div>
          <ul
            className="border border-gray-200 rounded bg-white divide-y divide-gray-100"
            data-testid="missing-list"
          >
            {rows.map((r) => (
              <li
                key={r.employee_id}
                className="px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3"
                data-testid={`missing-row-${r.employee_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.full_name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {r.username}
                    {r.branch_name && <> · {r.branch_name}</>}
                  </div>
                </div>
                <MissingStatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function MissingStatusBadge({ status }: { status: "draft" | "rejected" | null }) {
  if (status === "draft") {
    return (
      <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">
        szkic
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="text-xs font-medium px-2 py-0.5 rounded bg-rose-100 text-rose-800 whitespace-nowrap">
        odrzucony
      </span>
    );
  }
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
      brak wniosku
    </span>
  );
}

function GridPreview({ req }: { req: SelfAllocationRequest }) {
  // Build 10-tile preview from the (n×10%) lines.
  const tiles = useMemo(() => {
    const out: { short: string; klass: string }[] = [];
    // A line is labelled by its group (nowe wnioski) or its project (historyczne).
    const shortOf = (line: (typeof req.lines)[number]) =>
      line.project_group_short_name ?? line.project_short_name ?? "—";
    const sorted = [...req.lines].sort((a, b) =>
      naturalSortKey(shortOf(a)).localeCompare(naturalSortKey(shortOf(b))),
    );
    sorted.forEach((line, idx) => {
      const slots = Math.round(parseFloat(line.fraction) * 10);
      for (let i = 0; i < slots && out.length < 10; i++) {
        out.push({
          short: shortOf(line),
          klass: PREVIEW_COLORS[idx % PREVIEW_COLORS.length],
        });
      }
    });
    while (out.length < 10) {
      out.push({ short: "—", klass: "bg-white border-dashed border-gray-300 text-gray-400" });
    }
    return out;
  }, [req.lines]);

  return (
    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded" data-testid="grid-preview">
      <div className="text-xs text-blue-800 mb-2 font-medium">
        Podgląd graficzny (×10%) — pracownik wpisał ułamki w przyjaznej siatce
      </div>
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1 sm:gap-1.5">
        {tiles.map((t, i) => (
          <div
            key={i}
            className={`h-12 rounded border-2 font-mono flex flex-col items-center justify-center overflow-hidden px-0.5 ${t.klass}`}
            title={`${(i + 1) * 10}% — ${t.short}`}
          >
            <span className="text-[9px] uppercase tracking-widest opacity-70">
              {(i + 1) * 10}
            </span>
            <span className="text-[10px] sm:text-xs font-semibold w-full text-center truncate">
              {t.short}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
